import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import createHelpSchema from "./validations/helpSchema";

import { postHelp } from "../../api/helpReq";
import { useTranslation } from "../../i18n/TranslationProvider";
import { useAuth } from "../../contexts/AuthContext";
import { getLandingRoute } from "../../routes/routes";

const HelpPage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { isAuthenticated, userProfile, hasRoster } = useAuth();

    const helpSchema = useMemo(() => createHelpSchema(t), [t]);

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, isSubmitted, isSubmitSuccessful },
    } = useForm({
        resolver: yupResolver(helpSchema),
    });

    const onSubmit = async (data) => {
        try {
            await postHelp(data);
        } catch (err) {
            console.error("Help Page onSubmit Error:", err);
        }
    };

    let statusMessage = null;
    if (isSubmitSuccessful) {
        statusMessage = {
            text: t("help.successMessage"),
            tone: "success",
        };
    } else if (isSubmitted && !isSubmitSuccessful) {
        statusMessage = {
            text: t("help.errorMessage"),
            tone: "error",
        };
    }

    const returnPath = isAuthenticated ? getLandingRoute(userProfile, hasRoster) : "/";
    const returnLabel = isAuthenticated
        ? t("common.returnToDashboard")
        : t("common.returnHome");

    return (
        <div className="flex justify-center px-4 py-12">
            <div className="w-full max-w-lg bg-white rounded-2xl shadow-md p-8">
                <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
                    {t("help.heading")}
                </h1>
                <p className="text-center text-gray-500 mb-6">
                    {t("help.subtext")}
                </p>

                <div className="flex justify-center mb-6">
                    <button
                        type="button"
                        onClick={() => navigate("/feature-suggestion")}
                        className="text-sm font-semibold text-teal-600 hover:text-teal-700 underline underline-offset-2"
                    >
                        {t("help.newFeatureSuggestionButton")}
                    </button>
                </div>

                {statusMessage && (
                    <div
                        className={`mb-6 rounded-lg px-4 py-3 text-sm text-center border ${
                            statusMessage.tone === "success"
                                ? "bg-teal-50 text-teal-700 border-teal-200"
                                : "bg-red-50 text-red-700 border-red-200"
                        }`}
                    >
                        {statusMessage.text}
                    </div>
                )}

                {isSubmitSuccessful && (
                    <div className="flex justify-center">
                        <button
                            type="button"
                            onClick={() => navigate(returnPath)}
                            className="px-6 py-2 rounded-full font-semibold text-white bg-teal-600 hover:bg-teal-700"
                        >
                            {returnLabel}
                        </button>
                    </div>
                )}

                {!isSubmitSuccessful && (
                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div>
                            <label
                                htmlFor="email"
                                className="block text-sm font-semibold text-gray-700 mb-1"
                            >
                                {t("auth.email")}
                            </label>
                            <input
                                id="email"
                                type="email"
                                autoFocus
                                {...register("email")}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {errors.email && (
                                <p className="text-red-600 text-xs mt-1">
                                    {errors.email.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label
                                htmlFor="name"
                                className="block text-sm font-semibold text-gray-700 mb-1"
                            >
                                {t("common.nameLabel")}
                            </label>
                            <input
                                id="name"
                                type="text"
                                {...register("name")}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {errors.name && (
                                <p className="text-red-600 text-xs mt-1">
                                    {errors.name.message}
                                </p>
                            )}
                        </div>

                        <div>
                            <label
                                htmlFor="ticket"
                                className="block text-sm font-semibold text-gray-700 mb-1"
                            >
                                {t("help.ticketLabel")}
                            </label>
                            <p className="text-xs text-gray-500 mb-1">
                                {t("help.ticketHint")}
                            </p>
                            <textarea
                                id="ticket"
                                rows={5}
                                {...register("ticket")}
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                            {errors.ticket && (
                                <p className="text-red-600 text-xs mt-1">
                                    {errors.ticket.message}
                                </p>
                            )}
                        </div>

                        <div className="flex justify-between pt-2">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                disabled={isSubmitting}
                                className="px-4 py-2 rounded-full font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                            >
                                {t("common.goBack")}
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2 rounded-full font-semibold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
                            >
                                {isSubmitting ? t("common.sending") : t("athleteSignup.submit")}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default HelpPage;